<template>
  <div
    class="relative max-w-full"
    :class="{ 'animate-pulse': loading }"
    :data-product-card-id="product.id"
  >
    <slot>
      <Intersect
        :threshold="0.5"
        @enter="$emit('intersect:product', product.id)"
      >
        <article :id="`product-${product.id}`" class="group">
          <slot name="header">
            <div
              class="group relative max-h-[768px] bg-white"
              :style="{ aspectRatio: '7/10' }"
            >
              <div class="pointer-events-auto overflow-hidden">
                <slot
                  name="header-image"
                  :image="frontImage"
                  :product-name="name"
                >
                  <NuxtLink
                    :to="!isInsideStoryblok ? link : undefined"
                    :aria-label="name"
                    class="group flex items-end"
                    @click.capture="$emit('click:product')"
                  >
                    <ProductImage
                      v-if="frontImage"
                      fit="contain"
                      :image="frontImage"
                      :alt="name"
                      sizes="sm:400px"
                      :should-trim="false"
                      is-centered
                      class="absolute inset-0 group-focus:ring-2 group-focus:ring-gray-secondary"
                      :class="{ 'opacity-100': !isAvailable }"
                    />
                    <div
                      v-if="secondaryImage && !isMobile"
                      class="absolute inset-0"
                    >
                      <ProductImage
                        :image="secondaryImage"
                        :alt="name"
                        sizes="sm:400px"
                        fit="cover"
                        class="absolute inset-0 opacity-0 hover-native-only:group-hover:opacity-100"
                        low-priority
                      />
                    </div>
                    <div
                      v-if="sizeCategories"
                      class="md:min-h-1/6 min-h-2/6 font-sm absolute -bottom-0.5 left-0 z-30 hidden w-full bg-white-80 p-3 font-sans hover-native-only:group-hover:block"
                    >
                      <span class="font-medium">
                        {{ $t('product_card.available_in') }}
                      </span>
                      <div class="mt-1 font-light">{{ sizeCategories }}</div>
                    </div>
                  </NuxtLink>
                </slot>
              </div>
              <div class="pointer-events-none absolute">
                <slot name="header-badge" :label="badgeLabel">
                  <div
                    v-if="badgeLabel"
                    class="absolute left-0 top-0 hover-native-only:group-hover:hidden"
                  >
                    <ProductBadge :badge-label="badgeLabel" />
                  </div>
                </slot>
              </div>
              <div class="inset-x-0 top-0 flex justify-end">
                <slot v-if="product" name="header-actions">
                  <ProductCardHeaderActions
                    :product="product"
                    :product-name="name"
                    :wishlist-remove-cta="wishlistRemoveCta"
                    :list-type="listType"
                    :list-name="listName"
                    :list-index="listIndex"
                  />
                </slot>
              </div>
            </div>
          </slot>
          <slot
            name="description"
            v-bind="{ ...$props, brand, name, price, isAvailable, nameHtml }"
          >
            <div class="pb-0.5 pt-[9px] lg:pt-[15px]">
              <slot
                name="description-title-price"
                v-bind="{ ...$props, price, brand, nameHtml }"
              >
                <NuxtLink
                  class="font-sm text-sm leading-[14px] lg:text-lg lg:leading-6"
                  :to="!isInsideStoryblok ? link : undefined"
                  @click.capture="$emit('click:product')"
                >
                  <p class="truncate uppercase">{{ brand }}</p>
                  <!-- eslint-disable vue/no-v-html -->
                  <p
                    class="description truncate font-light"
                    data-qa="product-title"
                    v-html="nameHtml"
                  ></p>
                  <slot name="description-price" :price="price">
                    <ProductPrice
                      v-if="price"
                      :price="price"
                      :applied-reductions="price?.appliedReductions || []"
                      :show-price-reduction="showPriceReduction"
                      :highlight-price-red="highlightPriceRed"
                      type="whisper"
                      size="md"
                    />
                  </slot>
                </NuxtLink>
              </slot>

              <div v-if="!isCarouselItem">
                <div v-if="product" class="mt-1">
                  <slot name="description-action" :colors="colors">
                    <ProductColorPicker
                      :items="siblings"
                      :selected-id="product.id"
                      from-listing-page
                      :is-limiting="true"
                      :hide-more-colors-label="hideMoreColorsLabel"
                      class="product-color-picker__container"
                    >
                      <template #item="{ item }">
                        <NuxtLink
                          v-if="item"
                          :to="
                            toLocalePath(
                              getProductDetailRoute(product, item?.id),
                            )
                          "
                          :aria-label="item?.colors?.[0]?.label"
                          @click.capture="$emit('click:product')"
                        >
                          <ColorCircle
                            data-test-id="product-card-color-circle"
                            :color="item?.colors?.[0] as ProductColor"
                            :has-active-dot="item.id === product.id"
                            from-listing-page
                          />
                        </NuxtLink>
                      </template>
                      <template #further-items="{ count }">
                        <NuxtLink
                          v-if="count > 0"
                          :to="link"
                          class="self-center pl-1 text-xs font-medium leading-none underline lg:text-base"
                          @click.capture="$emit('click:product')"
                        >
                          +{{ count }} {{ $t('filter.colors') }}
                        </NuxtLink>
                      </template>
                    </ProductColorPicker>
                  </slot>
                </div>
              </div>
            </div>
          </slot>
        </article>
      </Intersect>
    </slot>
  </div>
</template>

<script setup lang="ts">
import type {
  Product,
  Category,
  Price,
  ProductColor,
  ProductImage as ProductImageType,
} from '@scayle/storefront-nuxt'
import { getProductSizeCategories } from '~/utils/product'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import { useStoryblokEditor } from '#layers/base/composables/cms/useStoryblokEditor'
import { useDeviceType } from '~/composables/useDeviceType'
import { getProductDetailRoute } from '~/utils/route'
import { shouldShowHoverImagesImmediately } from '#layers/base/utils/category'
import type { ListItemType } from '~/types/tracking'
import { useProductBaseInfo } from '#layers/base/composables/useProductBaseInfo'

type ProductCardProps = {
  product: Product
  loading?: boolean
  variantPrice?: Price
  badgeLabel?: string
  wishlistRemoveCta?: boolean
  isCarouselItem?: boolean
  showPriceReduction?: boolean
  listType?: ListItemType
  listName?: string
  listIndex?: number
  selectedCategory?: Category | null
  hideMoreColorsLabel?: boolean
  image?: ProductImageType
  hoverImage?: ProductImageType
  disableHoverImage?: boolean
  highlightPriceRed?: boolean
}

const props = withDefaults(defineProps<ProductCardProps>(), {
  variantPrice: undefined,
  selectedCategory: null,
  listIndex: undefined,
  badgeLabel: undefined,
  showPriceReduction: true,
  listType: undefined,
  listName: '',
  hideMoreColorsLabel: false,
  image: undefined,
  hoverImage: undefined,
  disableHoverImage: false,
  highlightPriceRed: false,
})

defineEmits<{ 'intersect:product': [value: number]; 'click:product': [] }>()

const {
  link,
  brand,
  name,
  price: productPrice,
  isAvailable,
  primaryImage,
  hoverImage,
  siblings,
  colors,
} = useProductBaseInfo(() => props.product)

const { isMobile } = useDeviceType()
const { toLocalePath } = useRouteHelpers()

const { isInStoryblokEditor: isInsideStoryblok } = useStoryblokEditor()

const price = computed(() => props.variantPrice || productPrice.value)
const sizeCategories = computed(() => {
  return getProductSizeCategories(props.product)
    .map(({ label }) => label)
    .join(', ')
})
const nameHtml = computed(() =>
  name.value.replace(/(LOUISA|MARTHA|CARLA|ANNA)/gi, '<b>$1</b>'),
)

const showHoverImagesImmediately = computed(() =>
  props.selectedCategory
    ? shouldShowHoverImagesImmediately(props.selectedCategory)
    : false,
)

const frontImage = computed(() => {
  if (
    showHoverImagesImmediately.value &&
    (props.hoverImage || hoverImage.value)
  ) {
    return props.hoverImage ?? hoverImage.value
  }
  return props.image ?? primaryImage.value
})

const secondaryImage = computed(() => {
  if (showHoverImagesImmediately.value) {
    return null
  }
  return props.hoverImage ?? (props.disableHoverImage ? null : hoverImage.value)
})
</script>

<style lang="scss" scoped>
.description b {
  font-weight: 900;
}
</style>
